import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  Repeat,
  X,
  DollarSign,
  Clock,
  BarChart2,
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  Shield,
  Calendar,
  Info,
  ArrowRight,
  RefreshCw,
  Sparkles,
  Check,
  ChevronRight,
  ChevronLeft,
  Eye,
  Zap,
  BriefcaseBusiness,
  Coins,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import * as alpaca from '@/services/alpaca';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { useSymbolSearch } from '@/hooks/market-data/useSymbolSearch';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { intelligentOrderService, IntelligentOrderRequest } from '@/services/intelligentOrderService';
import { deploymentService } from '@/services/deploymentService';
import { CreditService } from '@/services/creditService';
import { supabase } from '@/integrations/supabase/client';

interface OrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
}

interface OrderStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const ORDER_TYPES = [
  { value: 'market', label: 'Market', description: 'Execute immediately at market price' },
  { value: 'limit', label: 'Limit', description: 'Execute at specified price or better' },
  { value: 'stop', label: 'Stop', description: 'Convert to market order when price is reached' },
  { value: 'stop_limit', label: 'Stop Limit', description: 'Convert to limit order when stop price is reached' },
  { value: 'trailing_stop', label: 'Trailing Stop', description: 'Stop price follows market by specified amount' },
];

const TIME_IN_FORCE = [
  { value: 'day', label: 'Day', description: 'Valid for the trading day' },
  { value: 'gtc', label: 'GTC', description: 'Good till canceled' },
  { value: 'ioc', label: 'IOC', description: 'Immediate or cancel' },
  { value: 'fok', label: 'FOK', description: 'Fill or kill' },
];

const PRICE_STEPS = {
  low: 0.01,    // For stocks < $10
  medium: 0.05,  // For stocks $10-$100
  high: 0.10     // For stocks > $100
};

export const OrderDialog: React.FC<OrderDialogProps> = ({ open, onOpenChange, onSubmit }) => {
  const { toast } = useToast();
  
  // Define steps for the order workflow - following StepBasedStrategyDialog pattern
  const steps: OrderStep[] = [
    {
      id: 'symbol',
      title: 'Symbol & Side',
      description: 'Select symbol and trade direction',
      icon: <BriefcaseBusiness className="h-5 w-5" />,
    },
    {
      id: 'order',
      title: 'Order Details',
      description: 'Configure order type and sizing',
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      id: 'risk',
      title: 'Risk Management',
      description: 'Set stop loss and take profit',
      icon: <Shield className="h-5 w-5" />,
    },
    {
      id: 'advanced',
      title: 'Advanced Settings',
      description: 'Configure execution settings',
      icon: <BarChart2 className="h-5 w-5" />,
    },
    {
      id: 'review',
      title: 'Review & Submit',
      description: 'Review order and submit',
      icon: <Eye className="h-5 w-5" />,
    },
  ];

  // Current step state - following StepBasedStrategyDialog pattern
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = steps[currentStepIndex];
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  // Reset step index when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStepIndex(0);
      setValidationErrors({});
    }
  }, [open]);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isPaperMode, setIsPaperMode] = useState(
    process.env.VITE_ALPACA_PAPER === 'true'
  );
  
  // Add deployment mode state for paper/live trading (no backtest for simple orders)
  const [deploymentMode, setDeploymentMode] = useState<'paper' | 'live'>(
    process.env.VITE_ALPACA_PAPER === 'true' ? 'paper' : 'live'
  );
  
  // Update isPaperMode when deployment mode changes
  useEffect(() => {
    setIsPaperMode(deploymentMode === 'paper');
  }, [deploymentMode]);

  const [brokerAccount, setBrokerAccount] = useState('');
  const [symbolSearchText, setSymbolSearchText] = useState('');
  const [symbolData, setSymbolData] = useState<any>(null);
  
  // Basic order data
  const [orderData, setOrderData] = useState({
    symbol: '',
    quantity: 1,
    side: 'buy',
    type: 'market',
    timeInForce: 'day',
    limitPrice: '',
    stopPrice: '',
    trailingPercent: '',
    notional: '',
    useNotional: false,
  });
  
  // Risk management data
  const [riskData, setRiskData] = useState({
    enabled: false,
    stopLossType: 'percentage',
    stopLossValue: '2',
    takeProfitType: 'percentage',
    takeProfitValue: '5',
    trailingStopEnabled: false,
    trailingStopValue: '1',
    maxLoss: '5',
    riskRewardRatio: '2',
    useDynamicExits: false,
  });
  
  // Advanced settings
  const [advancedSettings, setAdvancedSettings] = useState({
    extendedHours: false,
    allOrNone: false,
    useMLDecisions: false,
    selectedModel: 'stewie_quant',
    confidenceThreshold: 70,
  });
  
  const { searchSymbols, results: searchResults, isLoading: searchLoading } = useSymbolSearch();
  
  // Fetch account information using our VM trading backend
  useEffect(() => {
    async function fetchAccount() {
      try {
        // Add timeout for localhost development to prevent hanging
        const isLocalhost = typeof window !== 'undefined' && 
          (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        
        const timeoutMs = isLocalhost ? 3000 : 10000; // 3 seconds for localhost, 10 seconds for production
        
        const withTimeout = (promise: Promise<any>) => {
          return Promise.race([
            promise,
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
            )
          ]);
        };

        // Use the deployment service which prioritizes VM and falls back to legacy API
        const acct = await withTimeout(deploymentService.getAccountInfo(deploymentMode));
        const acctNum = acct.account_id ?? acct.account_number;
        setBrokerAccount(acctNum);
        console.log('Successfully fetched account from VM:', acctNum);
      } catch (err) {
        console.error('Failed to fetch account information:', err);
        // For localhost development, use a fallback account to prevent blocking
        if (typeof window !== 'undefined' && 
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
          setBrokerAccount('DEMO_ACCOUNT');
          console.log('Using demo account for localhost development');
        } else {
          setBrokerAccount('ERROR_LOADING_ACCOUNT');
          toast({
            title: "Account Loading Error",
            description: "Unable to load account information from your VM. Please ensure your trading VM is running and configured correctly.",
            variant: "destructive",
          });
        }
      }
    }
    
    // Only fetch account if the dialog is open to avoid unnecessary API calls
    if (open) {
      fetchAccount();
    }
  }, [open, deploymentMode, toast]);

  // Fetch symbol data when symbol changes - using user's Alpaca market data subscription
  useEffect(() => {
    async function fetchSymbolData() {
      if (!orderData.symbol) {
        setSymbolData(null);
        return;
      }
      
      try {
        // Use the user's Alpaca market data subscription for real-time quotes
        console.log(`Fetching real market data for ${orderData.symbol} via user's Alpaca subscription`);
        const quoteData = await alpaca.marketData.getLatestQuote(orderData.symbol);
        
        // Set real market values based on actual quote data
        const askPrice = parseFloat(quoteData.ask.toString());
        const bidPrice = parseFloat(quoteData.bid.toString());
        const priceStep = askPrice < 10 ? PRICE_STEPS.low : 
                        askPrice < 100 ? PRICE_STEPS.medium : PRICE_STEPS.high;
        
        setSymbolData({
          askPrice,
          bidPrice,
          lastPrice: (askPrice + bidPrice) / 2,
          priceStep
        });
        
        // Set default limit price based on real market data
        setOrderData(prev => ({
          ...prev,
          limitPrice: askPrice.toFixed(2),
          stopPrice: (askPrice * 0.98).toFixed(2)
        }));
        
        console.log(`Real market data loaded for ${orderData.symbol}:`, {
          ask: askPrice,
          bid: bidPrice,
          last: (askPrice + bidPrice) / 2
        });
        
      } catch (err) {
        console.warn(`Failed to fetch market data for ${orderData.symbol}:`, err);
        // Set some defaults only if market data fails
        setSymbolData({
          askPrice: 100.05,
          bidPrice: 99.95,
          lastPrice: 100.00,
          priceStep: PRICE_STEPS.medium
        });
      }
    }
    
    fetchSymbolData();
  }, [orderData.symbol]);
  
  const handleSymbolSearch = async (query: string) => {
    setSymbolSearchText(query);
    
    // If the user clears the search input, also clear the selected symbol
    if (!query.trim()) {
      updateOrderData('symbol', '');
      setSymbolData(null);
    } else if (query.length >= 1) {
      // Start searching after 1 character
      await searchSymbols(query);
    }
  };
  
  const selectSymbol = (symbol: string) => {
    setOrderData(prev => ({ ...prev, symbol }));
    setSymbolSearchText('');
    // Clear any validation errors when a valid symbol is selected
    if (validationErrors.symbol) {
      setValidationErrors(prev => ({ ...prev, symbol: '' }));
    }
  };
  
  const updateOrderData = (key: string, value: any) => {
    setOrderData(prev => ({ ...prev, [key]: value }));
    // Clear validation error for this field
    if (validationErrors[key]) {
      setValidationErrors(prev => ({ ...prev, [key]: '' }));
    }
  };
  
  const updateRiskData = (key: string, value: any) => {
    setRiskData(prev => ({ ...prev, [key]: value }));
  };
  
  const updateAdvancedSettings = (key: string, value: any) => {
    setAdvancedSettings(prev => ({ ...prev, [key]: value }));
  };

  // Validation function - following StepBasedStrategyDialog pattern
  const validateCurrentStep = (): boolean => {
    const errors: Record<string, string> = {};
    
    switch (currentStep.id) {
      case 'symbol':
        if (!orderData.symbol) {
          errors.symbol = 'Symbol is required';
        }
        break;
        
      case 'order':
        if (orderData.useNotional) {
          const notional = parseFloat(orderData.notional || '0');
          if (!orderData.notional || isNaN(notional) || notional <= 0) {
            errors.notional = 'Dollar amount must be a valid number greater than 0';
          }
          if (notional > 1000000) {
            errors.notional = 'Dollar amount cannot exceed $1,000,000';
          }
        } else {
          const quantity = parseInt(orderData.quantity?.toString() || '0');
          if (!orderData.quantity || isNaN(quantity) || quantity <= 0) {
            errors.quantity = 'Quantity must be a valid number greater than 0';
          }
          if (quantity > 10000) {
            errors.quantity = 'Quantity cannot exceed 10,000 shares';
          }
        }
        
        // Validate required price fields
        if (orderData.type === 'limit' || orderData.type === 'stop_limit') {
          if (!orderData.limitPrice) {
            errors.limitPrice = 'Limit price is required for this order type';
          } else {
            const limitPrice = parseFloat(orderData.limitPrice);
            if (isNaN(limitPrice) || limitPrice <= 0) {
              errors.limitPrice = 'Limit price must be a valid number greater than 0';
            }
          }
        }
        
        if (orderData.type === 'stop' || orderData.type === 'stop_limit') {
          if (!orderData.stopPrice) {
            errors.stopPrice = 'Stop price is required for this order type';
          } else {
            const stopPrice = parseFloat(orderData.stopPrice);
            if (isNaN(stopPrice) || stopPrice <= 0) {
              errors.stopPrice = 'Stop price must be a valid number greater than 0';
            }
          }
        }
        
        if (orderData.type === 'trailing_stop') {
          if (!orderData.trailingPercent) {
            errors.trailingPercent = 'Trailing percent is required for trailing stop orders';
          } else {
            const trailingPercent = parseFloat(orderData.trailingPercent);
            if (isNaN(trailingPercent) || trailingPercent <= 0 || trailingPercent >= 100) {
              errors.trailingPercent = 'Trailing percent must be between 0 and 100';
            }
          }
        }
        break;
        
      case 'risk':
        if (riskData.enabled) {
          const stopLossValue = parseFloat(riskData.stopLossValue || '0');
          if (!riskData.stopLossValue || isNaN(stopLossValue) || stopLossValue <= 0) {
            errors.stopLossValue = 'Stop loss value must be a valid number greater than 0';
          }
          if (stopLossValue > 50) {
            errors.stopLossValue = 'Stop loss value cannot exceed 50%';
          }
          
          const takeProfitValue = parseFloat(riskData.takeProfitValue || '0');
          if (!riskData.takeProfitValue || isNaN(takeProfitValue) || takeProfitValue <= 0) {
            errors.takeProfitValue = 'Take profit value must be a valid number greater than 0';
          }
          if (takeProfitValue > 200) {
            errors.takeProfitValue = 'Take profit value cannot exceed 200%';
          }
          
          // Validate risk-reward ratio
          if (!isNaN(stopLossValue) && !isNaN(takeProfitValue) && takeProfitValue < stopLossValue * 0.5) {
            errors.takeProfitValue = 'Take profit should be at least 50% of stop loss for reasonable risk-reward ratio';
          }
        }
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Navigation functions - following StepBasedStrategyDialog pattern
  const goToNextStep = () => {
    if (!validateCurrentStep()) return;
    
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  // Complete JSON building function - improved from original
  const buildCompleteOrderJSON = () => {
    try {
      // Create order ID
      const orderId = `order_${orderData.symbol}_${Date.now()}`;
      
      // Base order data
      const finalOrderData: any = {
        order_id: orderId,
        order: {
          symbol: orderData.symbol,
          qty: orderData.useNotional ? undefined : orderData.quantity,
          notional: orderData.useNotional ? parseFloat(orderData.notional) : undefined,
          side: orderData.side,
          type: orderData.type,
          time_in_force: orderData.timeInForce,
        },
        metadata: {
          created_at: new Date().toISOString(),
          broker_account: brokerAccount,
          is_paper_trading: isPaperMode,
          order_source: 'stewietrader_ui',
        }
      };
      
      // Add price fields based on order type
      if (orderData.type === 'limit' || orderData.type === 'stop_limit') {
        finalOrderData.order.limit_price = parseFloat(orderData.limitPrice);
      }
      
      if (orderData.type === 'stop' || orderData.type === 'stop_limit') {
        finalOrderData.order.stop_price = parseFloat(orderData.stopPrice);
      }
      
      if (orderData.type === 'trailing_stop') {
        finalOrderData.order.trail_percent = parseFloat(orderData.trailingPercent);
      }
      
      // Add risk management if enabled
      if (riskData.enabled) {
        finalOrderData.order.order_class = 'bracket';
        
        // Calculate stop loss and take profit prices
        const currentPrice = symbolData?.lastPrice || 100;
        const stopLossPrice = orderData.side === 'buy' 
          ? currentPrice * (1 - parseFloat(riskData.stopLossValue) / 100)
          : currentPrice * (1 + parseFloat(riskData.stopLossValue) / 100);
        
        const takeProfitPrice = orderData.side === 'buy'
          ? currentPrice * (1 + parseFloat(riskData.takeProfitValue) / 100)
          : currentPrice * (1 - parseFloat(riskData.takeProfitValue) / 100);
        
        finalOrderData.order.take_profit = {
          limit_price: takeProfitPrice.toFixed(2)
        };
        
        finalOrderData.order.stop_loss = {
          stop_price: stopLossPrice.toFixed(2),
          limit_price: riskData.stopLossType === 'stop_limit' ? stopLossPrice.toFixed(2) : undefined
        };
        
        // Add risk management settings
        finalOrderData.risk_management = {
          enabled: riskData.enabled,
          stop_loss_type: riskData.stopLossType,
          stop_loss_value: parseFloat(riskData.stopLossValue),
          take_profit_type: riskData.takeProfitType,
          take_profit_value: parseFloat(riskData.takeProfitValue),
          trailing_stop_enabled: riskData.trailingStopEnabled,
          trailing_stop_value: parseFloat(riskData.trailingStopValue),
          use_dynamic_exits: riskData.useDynamicExits,
          risk_reward_ratio: parseFloat(riskData.takeProfitValue) / parseFloat(riskData.stopLossValue),
        };
      }
      
      // Add advanced settings
      finalOrderData.advanced_settings = {
        extended_hours: advancedSettings.extendedHours,
        all_or_none: advancedSettings.allOrNone,
        use_ml_decisions: advancedSettings.useMLDecisions,
        selected_model: advancedSettings.selectedModel,
        confidence_threshold: advancedSettings.confidenceThreshold,
      };
      
      // Add symbol metadata if available
      if (symbolData) {
        finalOrderData.symbol_data = {
          last_price: symbolData.lastPrice,
          ask_price: symbolData.askPrice,
          bid_price: symbolData.bidPrice,
          price_step: symbolData.priceStep,
        };
      }
      
      // Calculate estimated values
      const estimatedValue = orderData.useNotional 
        ? parseFloat(orderData.notional || '0')
        : (parseInt(orderData.quantity.toString()) * (symbolData?.lastPrice || 100));
      
      finalOrderData.estimates = {
        estimated_value: estimatedValue,
        estimated_commission: estimatedValue * 0.005, // Example commission
        estimated_total: estimatedValue + (estimatedValue * 0.005),
      };
      
      return finalOrderData;
      
    } catch (error) {
      console.error('Error building order JSON:', error);
      toast({
        title: "Error",
        description: "Failed to build order data. Please check your inputs.",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Get current user for credit deduction
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        throw new Error('User not authenticated');
      }
      
      // Calculate order value for credit deduction
      const orderValue = calculateOrderValue();
      const orderValueNum = parseFloat(orderValue.replace(/[$,]/g, ''));
      
      if (isNaN(orderValueNum) || orderValueNum <= 0) {
        throw new Error('Invalid order value for credit calculation');
      }

      // Check if user has sufficient credits
      const creditCheck = await CreditService.checkSufficientCredits(session.user.id, orderValueNum);
      
      if (!creditCheck.sufficient) {
        toast({
          title: "Insufficient Credits",
          description: `You need ${creditCheck.required.toFixed(2)} credits but only have ${creditCheck.available.toFixed(2)}. Order value: $${orderValueNum.toLocaleString()}`,
          variant: "destructive",
        });
        return;
      }
      
      // Create intelligent order request with strategy context
      const intelligentRequest: IntelligentOrderRequest = {
        // Basic order info
        symbol: orderData.symbol,
        quantity: orderData.useNotional ? 
          Math.floor(parseFloat(orderData.notional || '0') / (symbolData?.lastPrice || 100)) : 
          parseInt(orderData.quantity.toString()),
        side: orderData.side as 'buy' | 'sell',
        orderType: orderData.type as any,
        limitPrice: orderData.type === 'limit' || orderData.type === 'stop_limit' ? 
          parseFloat(orderData.limitPrice) : undefined,
        stopPrice: orderData.type === 'stop' || orderData.type === 'stop_limit' ? 
          parseFloat(orderData.stopPrice) : undefined,
        timeInForce: orderData.timeInForce,
        
        // CRITICAL: Pass the user's trading mode selection to backend
        tradingMode: deploymentMode,
        
        // Enhanced strategy context for intelligent processing
        strategyContext: intelligentOrderService.createBasicStrategyContext(),
        
        // Add risk parameters from the dialog
        orderSource: 'manual',
      };

      // If we have risk settings from the dialog, override the basic context
      if (riskData.enabled) {
        intelligentRequest.strategyContext!.riskParameters = {
          ...intelligentRequest.strategyContext!.riskParameters!,
          stopLossType: riskData.stopLossType,
          stopLossValue: riskData.stopLossValue,
          profitTargetType: riskData.takeProfitType,
          profitTargetValue: riskData.takeProfitValue
        };
      }

      // Add advanced settings context
      if (advancedSettings) {
        intelligentRequest.strategyContext!.executionSettings = {
          ...intelligentRequest.strategyContext!.executionSettings!,
          executeRthOnly: !advancedSettings.extendedHours,
          maxSlippagePct: 0.5,
          executionDelay: 0,
          useVwap: false
        };
      }

      console.log(`%c Intelligent Order Request`, 'background: #222; color: #bada55; font-size: 14px;');
      console.log(JSON.stringify(intelligentRequest, null, 2));
      
      // Use intelligent order service instead of direct Alpaca
      const response = await intelligentOrderService.placeIntelligentOrder(intelligentRequest);
      
      if (response.success) {
        // Order was successful, now deduct credits
        const creditDeduction = await CreditService.deductCreditsForTrade({
          userId: session.user.id,
          aum: orderValueNum,
          eventType: 'order',
          description: `${orderData.side.toUpperCase()} order placed`,
          symbol: orderData.symbol,
          metadata: {
            order_id: response.executedOrder?.id,
            order_type: orderData.type,
            quantity: intelligentRequest.quantity,
            deployment_mode: deploymentMode,
            original_order_value: orderValueNum
          }
        });

        if (!creditDeduction.success) {
          console.error('Credit deduction failed:', creditDeduction.error);
          toast({
            title: "Warning: Order Executed",
            description: `Order was placed successfully, but there was an issue with credit deduction: ${creditDeduction.error}`,
            variant: "destructive",
          });
        } else {
          // Show success toast with credit information
          toast({
            title: "Order Placed Successfully",
            description: `${orderData.side === 'buy' ? 'Buy' : 'Sell'} order for ${orderData.symbol} placed. ${creditDeduction.creditsDeducted.toFixed(2)} credits deducted.`,
          });
        }

        // Show intelligent adjustments if any
        if (response.adjustments && response.adjustments.originalQuantity !== response.adjustments.finalQuantity) {
          toast({
            title: "Order Quantity Adjusted",
            description: `Quantity adjusted from ${response.adjustments.originalQuantity} to ${response.adjustments.finalQuantity} shares for risk management.`,
            variant: "default",
          });
        }

        // Show risk adjustments if any
        if (response.adjustments?.riskAdjustments && response.adjustments.riskAdjustments.length > 0) {
          toast({
            title: "Risk Management Applied",
            description: response.adjustments.riskAdjustments.join(', '),
            variant: "default",
          });
        }
        
        // Call the onSubmit with enhanced data
        onSubmit({
          order: response.executedOrder,
          intelligentResponse: response,
          originalRequest: intelligentRequest,
          orderData,
          riskData,
          advancedSettings,
          creditDeduction
        });
      } else {
        throw new Error(response.message);
      }
      
      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to place intelligent order:', error);
      toast({
        title: "Order Failed",
        description: error instanceof Error ? error.message : "Failed to place order. Please check your inputs and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Helper functions
  const calculateOrderValue = () => {
    if (!symbolData) return '--';
    
    const price = orderData.type === 'limit' ? 
                 parseFloat(orderData.limitPrice) : 
                 symbolData.lastPrice;
                 
    if (orderData.useNotional) {
      return `$${parseFloat(orderData.notional || '0').toFixed(2)}`;
    } else {
      const qty = parseInt(orderData.quantity.toString());
      return `$${(qty * price).toFixed(2)}`;
    }
  };

  // Price formatter that respects symbol's price step
  const formatPrice = (price: number) => {
    if (!symbolData) return price.toFixed(2);
    const step = symbolData.priceStep;
    const decimalPlaces = step === 0.01 ? 2 : step === 0.05 ? 2 : 2;
    return price.toFixed(decimalPlaces);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[100vh]">
        <TooltipProvider>
          <DialogHeader>
            <DialogTitle>Place New Order</DialogTitle>
          </DialogHeader>
          
          {/* Progress Indicator - following StepBasedStrategyDialog pattern */}
          <div className="space-y-4 mt-2">
            <div className="hidden sm:flex items-center justify-between">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={cn(
                    "flex items-center",
                    index !== steps.length - 1 && "flex-1"
                  )}
                >
                  <div
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-full border-2",
                      currentStepIndex > index
                        ? "border-primary bg-primary text-primary-foreground"
                        : currentStepIndex === index
                        ? "border-primary text-primary"
                        : "border-muted-foreground/30 text-muted-foreground"
                    )}
                  >
                    {currentStepIndex > index ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  {index !== steps.length - 1 && (
                    <div
                      className={cn(
                        "h-0.5 flex-1",
                        currentStepIndex > index
                          ? "bg-primary"
                          : "bg-muted-foreground/30"
                      )}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="sm:hidden">
              <Progress value={progress} className="h-2" />
              <div className="mt-2 flex items-center justify-between text-sm">
                <div>
                  Step {currentStepIndex + 1} of {steps.length}
                </div>
                <div className="font-medium">{currentStep.title}</div>
              </div>
            </div>
          </div>
          
          <ScrollArea className={`mt-4 pr-4 ${symbolSearchText && !orderData.symbol ? 'max-h-[700px]' : 'max-h-[600px]'}`}>
            <div className="p-6">
              {/* Current step content */}
              {currentStep.id === 'symbol' && (
                <div className="space-y-6">
                  {/* Modern Trading Mode Selection - Only on symbol step */}
                  <div className="border-b pb-6 mb-6">
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Trading Mode</h3>
                      
                      {/* Credit Cost Display */}
                      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Coins className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                              Estimated Credit Cost
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-blue-700 dark:text-blue-200">
                              {(() => {
                                const orderValue = calculateOrderValue();
                                const orderValueNum = parseFloat(orderValue.replace(/[$,]/g, ''));
                                if (isNaN(orderValueNum)) return '0.00';
                                const credits = CreditService.calculateCreditsRequired(orderValueNum);
                                return credits.toFixed(2);
                              })()} Credits
                            </div>
                            <div className="text-xs text-blue-600 dark:text-blue-400">
                              Order Value: {calculateOrderValue()}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                          Credits are charged at 10 basis points (0.1%) of tradeable AUM
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <Card 
                          className={`cursor-pointer border-2 transition-all ${deploymentMode === 'paper' ? 'border-primary bg-muted' : 'hover:border-muted-foreground'}`}
                          onClick={() => setDeploymentMode('paper')}
                        >
                          <CardHeader className="p-3 pb-0">
                            <CardTitle className="text-base flex items-center">
                              <Eye className="h-4 w-4 mr-1.5" />
                              Paper Trade
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3 pt-1">
                            <p className="text-xs text-muted-foreground">
                              Simulate trading with real-time market data
                            </p>
                          </CardContent>
                        </Card>
                        
                        <Card 
                          className={`cursor-pointer border-2 transition-all ${deploymentMode === 'live' ? 'border-primary bg-muted' : 'hover:border-muted-foreground'}`}
                          onClick={() => setDeploymentMode('live')}
                        >
                          <CardHeader className="p-3 pb-0">
                            <CardTitle className="text-base flex items-center">
                              <Zap className="h-4 w-4 mr-1.5" />
                              Live Trade
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3 pt-1">
                            <p className="text-xs text-muted-foreground">
                              Trade with real money on actual markets
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                      
                      <Card className="bg-muted/50">
                        <CardContent className="pt-4">
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Account ID:</span>
                              <span className={`font-mono text-sm ${brokerAccount === 'ERROR_LOADING_ACCOUNT' ? 'text-red-600' : ''}`}>
                                {brokerAccount === 'ERROR_LOADING_ACCOUNT' ? 'Error Loading Account' : (brokerAccount || 'Loading...')}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Broker:</span>
                              <span className="font-medium">Alpaca</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Current Mode:</span>
                              <span className="font-medium flex items-center">
                                <Badge variant={isPaperMode ? "secondary" : "destructive"}>
                                  {isPaperMode ? 'PAPER' : 'LIVE'}
                                </Badge>
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Connection:</span>
                              <span className={`font-medium flex items-center ${brokerAccount === 'ERROR_LOADING_ACCOUNT' ? 'text-red-600' : 'text-green-600'}`}>
                                {brokerAccount === 'ERROR_LOADING_ACCOUNT' ? (
                                  <>
                                    <X className="h-4 w-4 mr-1" />
                                    Error
                                  </>
                                ) : (
                                  <>
                                    <Check className="h-4 w-4 mr-1" />
                                    Connected
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {deploymentMode === 'live' && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                          <div className="flex items-start">
                            <AlertTriangle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-red-800">Live Trading Mode</p>
                              <p className="text-sm text-red-700 mt-1">
                                This order will use real money. Ensure all settings are correct before proceeding.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-base font-medium mb-4 flex items-center gap-2">
                      <BriefcaseBusiness className="text-blue-600" size={18} />
                      Symbol & Direction
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Symbol search */}
                      <div className="relative z-10">
                        <Label htmlFor="symbol" className="block text-sm font-medium mb-1">Symbol</Label>
                        <div className="relative">
                          <Input
                            id="symbol"
                            placeholder="Search symbol..."
                            value={symbolSearchText || (orderData.symbol ? '' : '')}
                            onChange={(e) => handleSymbolSearch(e.target.value)}
                            className="w-full rounded-lg"
                          />
                          
                          {/* Show selected symbol as a badge when symbol is selected and not searching */}
                          {orderData.symbol && !symbolSearchText && (
                            <div className="absolute inset-0 flex items-center px-3 bg-gray-50 border rounded-lg">
                              <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                                    {orderData.symbol}
                                  </Badge>
                                  <span className="text-sm text-gray-600">Selected</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSymbolSearchText(orderData.symbol);
                                      updateOrderData('symbol', '');
                                    }}
                                    className="h-6 px-2 text-xs hover:bg-blue-100"
                                  >
                                    Change
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      updateOrderData('symbol', '');
                                      setSymbolSearchText('');
                                      setSymbolData(null);
                                    }}
                                    className="h-6 px-2 text-xs hover:bg-red-100 text-red-600"
                                  >
                                    <X size={12} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Search results dropdown */}
                        {symbolSearchText && !orderData.symbol && (
                          <div className="absolute z-[9999] mt-1 w-full bg-white border-2 border-blue-200 rounded-lg shadow-2xl max-h-60 overflow-auto ring-1 ring-black ring-opacity-5">
                            {searchLoading ? (
                              <div className="p-3 text-center text-gray-500">
                                <RefreshCw className="inline-block animate-spin mr-2" size={14} />
                                Searching...
                              </div>
                            ) : searchResults.length > 0 ? (
                              <ul className="py-1">
                                {searchResults.map((item) => (
                                  <li 
                                    key={item.symbol}
                                    onClick={() => selectSymbol(item.symbol)}
                                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                  >
                                    <div className="font-medium text-gray-900">{item.symbol}</div>
                                    <div className="text-xs text-gray-500 truncate">{item.name}</div>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="p-3 text-center text-gray-500">
                                No symbols found
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Enhanced help text */}
                        {!orderData.symbol && !symbolSearchText && (
                          <p className="text-xs text-gray-500 mt-1">
                            Start typing to search for a stock symbol (e.g., AAPL, TSLA, MSFT)
                          </p>
                        )}
                        
                        {validationErrors.symbol && (
                          <p className="text-red-600 text-xs mt-1">{validationErrors.symbol}</p>
                        )}
                      </div>
                      
                      {/* Side selection */}
                      <div>
                        <Label className="block text-sm font-medium mb-1">Side</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant={orderData.side === 'buy' ? 'default' : 'outline'}
                            className={`rounded-lg flex items-center justify-center ${
                              orderData.side === 'buy' ? 'bg-green-600 hover:bg-green-700 text-white' : ''
                            }`}
                            onClick={() => updateOrderData('side', 'buy')}
                          >
                            <ArrowUpCircle className="mr-1.5 h-4 w-4" />
                            Buy
                          </Button>
                          <Button
                            type="button"
                            variant={orderData.side === 'sell' ? 'default' : 'outline'}
                            className={`rounded-lg flex items-center justify-center ${
                              orderData.side === 'sell' ? 'bg-red-600 hover:bg-red-700 text-white' : ''
                            }`}
                            onClick={() => updateOrderData('side', 'sell')}
                          >
                            <ArrowDownCircle className="mr-1.5 h-4 w-4" />
                            Sell
                          </Button>
                        </div>
                      </div>
                      
                      {/* Quantity or notional toggle */}
                      <div>
                        <div className="flex justify-between mb-1">
                          <Label className="text-sm font-medium">Size</Label>
                          <div className="flex items-center gap-2 text-xs">
                            <span className={!orderData.useNotional ? 'font-semibold' : ''}>Shares</span>
                            <Switch
                              checked={orderData.useNotional}
                              onCheckedChange={(checked) => updateOrderData('useNotional', checked)}
                            />
                            <span className={orderData.useNotional ? 'font-semibold' : ''}>Dollars</span>
                          </div>
                        </div>
                        
                        {orderData.useNotional ? (
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={16} />
                            <Input
                              type="number"
                              placeholder="Dollar amount"
                              className="pl-8 rounded-lg"
                              value={orderData.notional}
                              onChange={(e) => updateOrderData('notional', e.target.value)}
                            />
                            {validationErrors.notional && (
                              <p className="text-red-600 text-xs mt-1">{validationErrors.notional}</p>
                            )}
                          </div>
                        ) : (
                          <div>
                            <Input
                              type="number"
                              min="1"
                              placeholder="Number of shares"
                              className="rounded-lg"
                              value={orderData.quantity}
                              onChange={(e) => updateOrderData('quantity', e.target.value)}
                            />
                            {validationErrors.quantity && (
                              <p className="text-red-600 text-xs mt-1">{validationErrors.quantity}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Quote data if symbol is selected */}
                  {symbolData && orderData.symbol && (
                    <div className="mt-6 p-4 bg-gray-50 border rounded-lg">
                      <h4 className="text-sm font-medium mb-2">Quote Data</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Last Price:</span>
                          <div className="font-medium">${symbolData.lastPrice.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Bid:</span>
                          <div className="font-medium">${symbolData.bidPrice.toFixed(2)}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Ask:</span>
                          <div className="font-medium">${symbolData.askPrice.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {currentStep.id === 'order' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-base font-medium mb-4 flex items-center gap-2">
                      <DollarSign className="text-blue-600" size={18} />
                      Order Details
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="order-type" className="block text-sm font-medium mb-1">Order Type</Label>
                        <Select 
                          value={orderData.type} 
                          onValueChange={(val) => updateOrderData('type', val)}
                        >
                          <SelectTrigger id="order-type" className="w-full rounded-lg">
                            <SelectValue placeholder="Select order type" />
                          </SelectTrigger>
                          <SelectContent>
                            {ORDER_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <div>
                                  <span className="font-medium">{type.label}</span>
                                  <p className="text-xs text-gray-500">{type.description}</p>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="time-in-force" className="block text-sm font-medium mb-1">Time In Force</Label>
                        <Select 
                          value={orderData.timeInForce} 
                          onValueChange={(val) => updateOrderData('timeInForce', val)}
                        >
                          <SelectTrigger id="time-in-force" className="w-full rounded-lg">
                            <SelectValue placeholder="Select time in force" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_IN_FORCE.map((tif) => (
                              <SelectItem key={tif.value} value={tif.value}>
                                <div>
                                  <span className="font-medium">{tif.label}</span>
                                  <p className="text-xs text-gray-500">{tif.description}</p>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Conditional fields based on order type */}
                    {(orderData.type === 'limit' || orderData.type === 'stop_limit') && (
                      <div className="mt-4">
                        <Label htmlFor="limit-price" className="block text-sm font-medium mb-1">
                          Limit Price
                        </Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={16} />
                          <Input
                            id="limit-price"
                            type="number"
                            step={symbolData?.priceStep || 0.01}
                            className="pl-8 rounded-lg"
                            value={orderData.limitPrice}
                            onChange={(e) => updateOrderData('limitPrice', e.target.value)}
                          />
                          {validationErrors.limitPrice && (
                            <p className="text-red-600 text-xs mt-1">{validationErrors.limitPrice}</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {(orderData.type === 'stop' || orderData.type === 'stop_limit') && (
                      <div className="mt-4">
                        <Label htmlFor="stop-price" className="block text-sm font-medium mb-1">
                          Stop Price
                        </Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={16} />
                          <Input
                            id="stop-price"
                            type="number"
                            step={symbolData?.priceStep || 0.01}
                            className="pl-8 rounded-lg"
                            value={orderData.stopPrice}
                            onChange={(e) => updateOrderData('stopPrice', e.target.value)}
                          />
                          {validationErrors.stopPrice && (
                            <p className="text-red-600 text-xs mt-1">{validationErrors.stopPrice}</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {orderData.type === 'trailing_stop' && (
                      <div className="mt-4">
                        <Label htmlFor="trailing-percent" className="block text-sm font-medium mb-1">
                          Trailing Percent
                        </Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                          <Input
                            id="trailing-percent"
                            type="number"
                            min="0.01"
                            step="0.01"
                            max="100"
                            className="pl-8 rounded-lg"
                            value={orderData.trailingPercent}
                            onChange={(e) => updateOrderData('trailingPercent', e.target.value)}
                          />
                          {validationErrors.trailingPercent && (
                            <p className="text-red-600 text-xs mt-1">{validationErrors.trailingPercent}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {currentStep.id === 'risk' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-medium flex items-center gap-2">
                      <Shield className="text-blue-600" size={18} />
                      Risk Management
                    </h3>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id="enable-risk"
                        checked={riskData.enabled}
                        onCheckedChange={(checked) => updateRiskData('enabled', checked)}
                      />
                      <Label htmlFor="enable-risk" className="text-sm">
                        Enable Risk Management
                      </Label>
                    </div>
                  </div>
                  
                  {/* Risk management content */}
                  <div className={`space-y-6 ${!riskData.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Stop Loss settings */}
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center gap-2 mb-3">
                          <AlertTriangle className="text-red-500" size={16} />
                          <h4 className="text-sm font-medium">Stop Loss</h4>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="stop-loss-type" className="block text-sm font-medium mb-1">Type</Label>
                            <Select 
                              value={riskData.stopLossType} 
                              onValueChange={(val) => updateRiskData('stopLossType', val)}
                            >
                              <SelectTrigger id="stop-loss-type" className="w-full rounded-lg">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage</SelectItem>
                                <SelectItem value="atr">ATR Multiple</SelectItem>
                                <SelectItem value="volatility">Volatility Based</SelectItem>
                                <SelectItem value="risk_reward">Risk/Reward</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label htmlFor="stop-loss-value" className="block text-sm font-medium mb-1">
                              {riskData.stopLossType === 'percentage' ? 'Percentage (%)' :
                               riskData.stopLossType === 'atr' ? 'ATR Multiple' :
                               riskData.stopLossType === 'volatility' ? 'Std Dev Multiple' :
                               'Risk Units'}
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                id="stop-loss-value"
                                type="number"
                                className="rounded-lg"
                                min="0"
                                step="0.1"
                                value={riskData.stopLossValue}
                                onChange={(e) => updateRiskData('stopLossValue', e.target.value)}
                              />
                              {riskData.stopLossType === 'percentage' && (
                                <span className="text-sm font-medium">%</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Stop loss visualization */}
                          {symbolData && riskData.stopLossType === 'percentage' && (
                            <div className="mt-2 p-2 bg-white rounded border">
                              <p className="text-xs text-gray-600 mb-1">Stop price with {riskData.stopLossValue}% loss:</p>
                              <p className="text-sm font-medium">
                                ${orderData.side === 'buy' 
                                  ? (symbolData.lastPrice * (1 - parseFloat(riskData.stopLossValue) / 100)).toFixed(2)
                                  : (symbolData.lastPrice * (1 + parseFloat(riskData.stopLossValue) / 100)).toFixed(2)
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Take Profit settings */}
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center gap-2 mb-3">
                          <Target className="text-green-500" size={16} />
                          <h4 className="text-sm font-medium">Take Profit</h4>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="take-profit-type" className="block text-sm font-medium mb-1">Type</Label>
                            <Select 
                              value={riskData.takeProfitType} 
                              onValueChange={(val) => updateRiskData('takeProfitType', val)}
                            >
                              <SelectTrigger id="take-profit-type" className="w-full rounded-lg">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage</SelectItem>
                                <SelectItem value="atr">ATR Multiple</SelectItem>
                                <SelectItem value="volatility">Volatility Based</SelectItem>
                                <SelectItem value="risk_reward">Risk/Reward</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label htmlFor="take-profit-value" className="block text-sm font-medium mb-1">
                              {riskData.takeProfitType === 'percentage' ? 'Percentage (%)' :
                               riskData.takeProfitType === 'atr' ? 'ATR Multiple' :
                               riskData.takeProfitType === 'volatility' ? 'Std Dev Multiple' :
                               'Reward Units'}
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                id="take-profit-value"
                                type="number"
                                className="rounded-lg"
                                min="0"
                                step="0.1"
                                value={riskData.takeProfitValue}
                                onChange={(e) => updateRiskData('takeProfitValue', e.target.value)}
                              />
                              {riskData.takeProfitType === 'percentage' && (
                                <span className="text-sm font-medium">%</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Take profit visualization */}
                          {symbolData && riskData.takeProfitType === 'percentage' && (
                            <div className="mt-2 p-2 bg-white rounded border">
                              <p className="text-xs text-gray-600 mb-1">Target price with {riskData.takeProfitValue}% gain:</p>
                              <p className="text-sm font-medium">
                                ${orderData.side === 'buy' 
                                  ? (symbolData.lastPrice * (1 + parseFloat(riskData.takeProfitValue) / 100)).toFixed(2)
                                  : (symbolData.lastPrice * (1 - parseFloat(riskData.takeProfitValue) / 100)).toFixed(2)
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Trailing Stop */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="text-blue-500" size={16} />
                          <h4 className="text-sm font-medium">Trailing Stop</h4>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            id="enable-trailing"
                            checked={riskData.trailingStopEnabled}
                            onCheckedChange={(checked) => updateRiskData('trailingStopEnabled', checked)}
                          />
                          <Label htmlFor="enable-trailing" className="text-sm">Enable</Label>
                        </div>
                      </div>
                      
                      <div className={`${!riskData.trailingStopEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div>
                          <Label className="text-sm font-medium">Trailing Distance (%)</Label>
                          <div className="flex gap-3 items-center mt-1">
                            <Slider
                              value={[parseFloat(riskData.trailingStopValue) || 1]}
                              onValueChange={(val) => updateRiskData('trailingStopValue', val[0].toString())}
                              min={0.1}
                              max={10}
                              step={0.1}
                              className="flex-1"
                            />
                            <Input
                              type="number"
                              min="0.1"
                              step="0.1"
                              value={riskData.trailingStopValue}
                              onChange={(e) => updateRiskData('trailingStopValue', e.target.value)}
                              className="w-20 rounded-lg"
                            />
                            <span className="text-sm font-medium">%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Risk Visualization */}
                    {symbolData && (
                      <div className="p-4 bg-gray-50 border rounded-lg">
                        <h4 className="text-sm font-medium mb-3">Risk Visualization</h4>
                        
                        <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
                          {/* Entry price marker */}
                          <div className="absolute top-0 bottom-0 w-[2px] bg-blue-500 left-1/2 z-10"></div>
                          
                          {/* Stop loss zone */}
                          {riskData.enabled && (
                            <div 
                              className={`absolute top-0 bottom-0 ${orderData.side === 'buy' ? 'left-0' : 'right-0'} bg-red-200`} 
                              style={{ 
                                width: `${Math.min(parseFloat(riskData.stopLossValue), 20)}%` 
                              }}
                            ></div>
                          )}
                          
                          {/* Take profit zone */}
                          {riskData.enabled && (
                            <div 
                              className={`absolute top-0 bottom-0 ${orderData.side === 'buy' ? 'right-0' : 'left-0'} bg-green-200`} 
                              style={{ 
                                width: `${Math.min(parseFloat(riskData.takeProfitValue), 20)}%` 
                              }}
                            ></div>
                          )}
                        </div>
                        
                        <div className="flex justify-between mt-1 text-xs text-gray-600">
                          {orderData.side === 'buy' ? (
                            <>
                              <span>Stop: ${(symbolData.lastPrice * (1 - parseFloat(riskData.stopLossValue) / 100)).toFixed(2)}</span>
                              <span>Entry: ${symbolData.lastPrice.toFixed(2)}</span>
                              <span>Target: ${(symbolData.lastPrice * (1 + parseFloat(riskData.takeProfitValue) / 100)).toFixed(2)}</span>
                            </>
                          ) : (
                            <>
                              <span>Target: ${(symbolData.lastPrice * (1 - parseFloat(riskData.takeProfitValue) / 100)).toFixed(2)}</span>
                              <span>Entry: ${symbolData.lastPrice.toFixed(2)}</span>
                              <span>Stop: ${(symbolData.lastPrice * (1 + parseFloat(riskData.stopLossValue) / 100)).toFixed(2)}</span>
                            </>
                          )}
                        </div>
                        
                        {/* Risk/reward ratio */}
                        <div className="mt-4 flex justify-between items-center text-sm">
                          <span className="text-gray-600">Risk/Reward Ratio:</span>
                          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                            1:{(parseFloat(riskData.takeProfitValue) / parseFloat(riskData.stopLossValue)).toFixed(2)}
                          </Badge>
                        </div>
                      </div>
                    )}
                    
                    {/* Dynamic Risk Management */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <BarChart2 className="text-purple-500" size={16} />
                          <h4 className="text-sm font-medium">Dynamic Risk Management</h4>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            id="dynamic-exits"
                            checked={riskData.useDynamicExits}
                            onCheckedChange={(checked) => updateRiskData('useDynamicExits', checked)}
                          />
                          <Label htmlFor="dynamic-exits" className="text-sm">
                            Use ML-based exits
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger type="button">
                                  <Info className="ml-1 h-3 w-3 text-gray-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs max-w-[200px]">
                                    Let Stewie's ML models dynamically adjust exit conditions based on market conditions
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </Label>
                        </div>
                      </div>
                      
                      <div className={`${!riskData.useDynamicExits ? 'opacity-50 pointer-events-none' : ''}`}>
                        <p className="text-sm text-gray-600 mb-4">
                          Dynamic exits allow our ML models to adjust your stops and targets based on real-time market conditions.
                        </p>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="ml-model" className="block text-sm font-medium mb-1">ML Model</Label>
                            <Select 
                              value={advancedSettings.selectedModel} 
                              onValueChange={(val) => updateAdvancedSettings('selectedModel', val)}
                            >
                              <SelectTrigger id="ml-model" className="w-full rounded-lg">
                                <SelectValue placeholder="Select model" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="stewie_quant">StewieQuant™</SelectItem>
                                <SelectItem value="lstm">LSTM</SelectItem>
                                <SelectItem value="xgboost">XGBoost</SelectItem>
                                <SelectItem value="ensemble">Ensemble</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label htmlFor="confidence" className="block text-sm font-medium mb-1">
                              Confidence Threshold
                            </Label>
                            <div className="flex gap-2 items-center">
                              <Slider
                                id="confidence"
                                value={[advancedSettings.confidenceThreshold]}
                                onValueChange={(val) => updateAdvancedSettings('confidenceThreshold', val[0])}
                                min={50}
                                max={95}
                                step={5}
                                className="flex-1"
                              />
                              <span className="text-sm font-medium">{advancedSettings.confidenceThreshold}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {currentStep.id === 'advanced' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-base font-medium mb-4 flex items-center gap-2">
                      <Calendar className="text-blue-600" size={18} />
                      Trading Hours
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50/50">
                        <Switch
                          id="extended-hours"
                          checked={advancedSettings.extendedHours}
                          onCheckedChange={(checked) => updateAdvancedSettings('extendedHours', checked)}
                        />
                        <Label htmlFor="extended-hours" className="text-sm font-medium text-gray-700 cursor-pointer">
                          Extended Hours Trading
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <Info className="ml-1 h-3 w-3 text-gray-400" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs max-w-[200px]">
                                  Allow order execution during pre-market (4:00-9:30 AM ET) and after-hours (4:00-8:00 PM ET)
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>
                      
                      <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50/50">
                        <Switch
                          id="all-or-none"
                          checked={advancedSettings.allOrNone}
                          onCheckedChange={(checked) => updateAdvancedSettings('allOrNone', checked)}
                        />
                        <Label htmlFor="all-or-none" className="text-sm font-medium text-gray-700 cursor-pointer">
                          All or None
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <Info className="ml-1 h-3 w-3 text-gray-400" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs max-w-[200px]">
                                  Order must be filled completely or not at all
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-base font-medium mb-4 flex items-center gap-2">
                      <BarChart2 className="text-blue-600" size={18} />
                      ML-Based Decision Support
                    </h3>
                    
                    <div className="rounded-lg border p-4 bg-gradient-to-r from-indigo-50 to-purple-50">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Sparkles className="text-purple-600" size={16} />
                          <h4 className="text-sm font-medium text-gray-900">StewieQuant™ AI</h4>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            id="ml-decisions"
                            checked={advancedSettings.useMLDecisions}
                            onCheckedChange={(checked) => updateAdvancedSettings('useMLDecisions', checked)}
                          />
                          <Label htmlFor="ml-decisions" className="text-sm font-medium">Enable</Label>
                        </div>
                      </div>
                      
                      <div className={`${!advancedSettings.useMLDecisions ? 'opacity-50 pointer-events-none' : ''}`}>
                        <p className="text-sm text-gray-700 mb-4">
                          Activate StewieQuant™ AI to analyze this stock and recommend optimal entry, exit, and position sizing.
                        </p>
                        
                        {symbolData && advancedSettings.useMLDecisions && (
                          <div className="grid grid-cols-2 gap-4 p-3 bg-white rounded-lg shadow-sm">
                            <div>
                              <h5 className="text-xs font-medium mb-2 text-gray-500">ENTRY RECOMMENDATION</h5>
                              <div className="space-y-1">
                                <div className="flex items-center">
                                  <div className={`w-3 h-3 rounded-full ${orderData.side === 'buy' ? 'bg-green-500' : 'bg-red-500'} mr-2`}></div>
                                  <span className="text-sm font-medium">{orderData.side === 'buy' ? 'BUY' : 'SELL'} at {symbolData.lastPrice.toFixed(2)}</span>
                                </div>
                                <div className="ml-5 text-xs text-gray-500">
                                  Confidence: 87%
                                </div>
                                <div className="ml-5 text-xs text-gray-500">
                                  Optimal size: {
                                    orderData.useNotional
                                    ? `$${(parseFloat(orderData.notional || '0') * 0.9).toFixed(2)}`
                                    : `${Math.max(1, Math.floor(orderData.quantity * 0.9))} shares`
                                  }
                                </div>
                              </div>
                            </div>
                            
                            <div>
                              <h5 className="text-xs font-medium mb-2 text-gray-500">EXIT TARGETS</h5>
                              <div className="space-y-1">
                                <div className="flex items-center">
                                  <Target className="text-green-500 w-4 h-4 mr-1" />
                                  <span className="text-sm">
                                    ${orderData.side === 'buy' 
                                      ? (symbolData.lastPrice * 1.15).toFixed(2)
                                      : (symbolData.lastPrice * 0.85).toFixed(2)
                                    }
                                  </span>
                                </div>
                                <div className="flex items-center">
                                  <AlertTriangle className="text-red-500 w-4 h-4 mr-1" />
                                  <span className="text-sm">
                                    ${orderData.side === 'buy' 
                                      ? (symbolData.lastPrice * 0.92).toFixed(2)
                                      : (symbolData.lastPrice * 1.08).toFixed(2)
                                    }
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Risk-reward: 1:1.88
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {currentStep.id === 'review' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-base font-medium mb-4 flex items-center gap-2">
                      <Eye className="text-blue-600" size={18} />
                      Review Order
                    </h3>
                    
                    <Card className="border border-gray-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Order Summary</CardTitle>
                        <CardDescription>
                          Review your order details before submission
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Order Details */}
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-sm font-medium mb-3 text-gray-700">Order Details</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Symbol:</span>
                                <span className="font-medium">{orderData.symbol || 'Not selected'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Side:</span>
                                <span className={`font-medium ${orderData.side === 'buy' ? 'text-green-600' : 'text-red-600'}`}>
                                  {orderData.side === 'buy' ? 'Buy' : 'Sell'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Type:</span>
                                <span className="font-medium">{ORDER_TYPES.find(t => t.value === orderData.type)?.label || orderData.type}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Size:</span>
                                <span className="font-medium">
                                  {orderData.useNotional 
                                    ? `$${parseFloat(orderData.notional || '0').toFixed(2)}` 
                                    : `${orderData.quantity} shares`}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Time in Force:</span>
                                <span className="font-medium">{TIME_IN_FORCE.find(t => t.value === orderData.timeInForce)?.label || orderData.timeInForce}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="text-sm font-medium mb-3 text-gray-700">Price & Risk</h4>
                            <div className="space-y-2 text-sm">
                              {symbolData && (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Last Price:</span>
                                    <span className="font-medium">${symbolData.lastPrice.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Estimated Value:</span>
                                    <span className="font-medium">
                                      ${orderData.useNotional 
                                        ? parseFloat(orderData.notional || '0').toFixed(2)
                                        : (parseInt(orderData.quantity.toString()) * symbolData.lastPrice).toFixed(2)}
                                    </span>
                                  </div>
                                </>
                              )}
                              {(orderData.type === 'limit' || orderData.type === 'stop_limit') && orderData.limitPrice && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Limit Price:</span>
                                  <span className="font-medium">${parseFloat(orderData.limitPrice).toFixed(2)}</span>
                                </div>
                              )}
                              {(orderData.type === 'stop' || orderData.type === 'stop_limit') && orderData.stopPrice && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Stop Price:</span>
                                  <span className="font-medium">${parseFloat(orderData.stopPrice).toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-600">Risk Management:</span>
                                <span className={`font-medium ${riskData.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                                  {riskData.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {riskData.enabled && (
                          <div className="pt-4 border-t">
                            <h4 className="text-sm font-medium mb-3 text-gray-700">Risk Management</h4>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Stop Loss:</span>
                                <div className="font-medium">{riskData.stopLossValue}%</div>
                              </div>
                              <div>
                                <span className="text-gray-600">Take Profit:</span>
                                <div className="font-medium">{riskData.takeProfitValue}%</div>
                              </div>
                              <div>
                                <span className="text-gray-600">Risk/Reward:</span>
                                <div className="font-medium">
                                  1:{(parseFloat(riskData.takeProfitValue) / parseFloat(riskData.stopLossValue)).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {advancedSettings.extendedHours && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-800">Extended Hours Trading Enabled</span>
                            </div>
                            <p className="text-xs text-amber-700 mt-1">
                              This order may execute during pre-market or after-hours sessions.
                            </p>
                          </div>
                        )}
                        
                        {isPaperMode && (
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Info className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-medium text-green-800">Paper Trading Mode</span>
                            </div>
                            <p className="text-xs text-green-700 mt-1">
                              This is a simulated order that will not affect real positions or balance.
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* JSON Preview */}
                    <Card className="border border-gray-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <BarChart2 className="h-5 w-5" />
                          Order JSON Preview
                        </CardTitle>
                        <CardDescription>
                          Complete order data that will be sent to the backend
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs font-mono max-h-64 overflow-y-auto">
                          <pre>{JSON.stringify(buildCompleteOrderJSON(), null, 2)}</pre>
                        </div>
                        <div className="mt-3 text-xs text-gray-600">
                          This JSON structure ensures complete order data for backend processing including metadata, risk management, and execution settings.
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="p-4 border-t bg-gray-100 flex-shrink-0">
            <div className="flex justify-between items-center w-full">
              <div className="text-sm text-gray-600">
                {brokerAccount === 'ERROR_LOADING_ACCOUNT' 
                  ? 'Cannot place order: Account information unavailable. Check VM connection.'
                  : orderData.symbol 
                    ? `${orderData.side === 'buy' ? 'Buying' : 'Selling'} ${orderData.symbol} on ${isPaperMode ? 'paper' : 'live'} account` 
                    : 'Complete all steps to place order'}
              </div>
              <div className="flex gap-3">
                {currentStepIndex > 0 && (
                  <Button variant="outline" onClick={goToPreviousStep}>
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                )}
                
                <Button variant="outline" onClick={() => onOpenChange(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
                  Cancel
                </Button>
                
                {currentStepIndex < steps.length - 1 ? (
                  <Button onClick={goToNextStep} className="px-4 py-2 rounded-lg text-sm">
                    Continue
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={!orderData.symbol || isSubmitting || brokerAccount === 'ERROR_LOADING_ACCOUNT'} 
                    className={`px-4 py-2 rounded-lg text-sm text-white ${
                      orderData.symbol && !isSubmitting && brokerAccount !== 'ERROR_LOADING_ACCOUNT' ? 'bg-gray-900 hover:bg-gray-800' : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Placing Order...
                      </>
                    ) : brokerAccount === 'ERROR_LOADING_ACCOUNT' ? (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Account Error
                      </>
                    ) : (
                      <>
                        Place Order
                        <Zap className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
};
